import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ROLE_LABEL, type Role } from '@leadgenos/shared';
import { useSession } from '../store/session';
import { useLeadQueue } from '../store/leadQueue';
import { api, type NotificationGroup } from '../lib/api';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  managerOnly?: boolean;
  adminOnly?: boolean;
}

/** Full previous-version navigation. Rep-visible items first, then manager/admin tools. */
const NAV: NavItem[] = [
  { to: '/', label: 'Home', icon: '🏠' },
  { to: '/leads', label: 'My Leads', icon: '📋' },
  { to: '/qualify', label: 'Qualify', icon: '✅' },
  { to: '/scripts', label: 'Scripts', icon: '📞' },
  { to: '/quick-answers', label: 'Quick Answers', icon: '💬' },
  { to: '/activity', label: 'Activity', icon: '📝' },
  { to: '/leaderboard', label: 'Leaderboard', icon: '🏆', managerOnly: true },
  { to: '/import-export', label: 'Import / Export', icon: '⇅', managerOnly: true },
  { to: '/assignment', label: 'Assignment', icon: '🎯', managerOnly: true },
  { to: '/duplicates', label: 'Duplicates', icon: '🧬', managerOnly: true },
  { to: '/team', label: 'Team', icon: '👥', managerOnly: true },
  { to: '/coaching', label: 'Coaching', icon: '🎓', managerOnly: true },
  { to: '/admin', label: 'Admin', icon: '⚙️', managerOnly: true },
  {
    to: '/admin/prospect-qualification',
    label: 'Prospect Qualification',
    icon: '🔬',
    adminOnly: true,
  },
];

/** Manager/admin "View as Rep" impersonation control shown in the header. */
function ViewAsControl() {
  const { isManager, actingAs, setActingAs, user } = useSession();
  const roster = useQuery({
    queryKey: ['roster'],
    queryFn: () => api.roster(),
    enabled: isManager(),
  });
  if (!isManager()) return null;
  const reps = (roster.data ?? []).filter((r) => r.role === 'REP');

  return (
    <select
      className="rounded-md border border-white/30 bg-navy px-2 py-1 text-xs text-white"
      value={actingAs?.id ?? ''}
      onChange={(e) => {
        const id = e.target.value;
        if (!id) return setActingAs(null);
        const rep = reps.find((r) => r.id === id);
        if (rep) setActingAs({ id: rep.id, name: rep.name });
      }}
      title="View the app as one of your reps"
    >
      <option value="">View as… ({user ? ROLE_LABEL[user.role] : ''})</option>
      {reps.map((r) => (
        <option key={r.id} value={r.id}>
          👁 {r.name}
        </option>
      ))}
    </select>
  );
}

/**
 * Watches the running container's image version. `loaded` freezes to the version this SPA was
 * first served with (i.e. what the current JS bundle actually is); `latest` polls the server so
 * a deploy is noticed even in a tab left open across a release. When they diverge the browser is
 * still running stale code — hashed assets are cached immutably, so only a reload picks up the
 * new bundle (index.html is served no-cache, so the reload always fetches the new asset hashes).
 */
function useVersionWatch() {
  const { data } = useQuery({
    queryKey: ['version'],
    queryFn: () => api.version(),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const latest = data?.version;

  const [loaded, setLoaded] = useState<string | null>(null);
  useEffect(() => {
    if (latest && loaded === null) setLoaded(latest);
  }, [latest, loaded]);

  return { loaded, latest, updateAvailable: !!(loaded && latest && latest !== loaded) };
}

/** Dismissable top banner prompting a reload when a newer release has been deployed. */
function UpdateBanner() {
  const { updateAvailable } = useVersionWatch();
  if (!updateAvailable) return null;

  return (
    <div className="flex items-center justify-center gap-3 bg-navy px-4 py-2 text-xs font-semibold text-white">
      <span>✨ A new version of LeadGENOS is available.</span>
      <button
        className="rounded bg-gold px-3 py-1 text-navy hover:bg-gold/90"
        onClick={() => window.location.reload()}
      >
        Reload now
      </button>
    </div>
  );
}

/** Slim footer showing the running container's image version (stamped by CI at build time). */
function VersionFooter() {
  const { loaded } = useVersionWatch();

  return (
    <footer className="flex items-center justify-between gap-2 border-t border-slate-200 bg-white px-4 py-1.5 text-[11px] text-slate-400">
      <span>© {new Date().getFullYear()} CGM · LeadGENOS</span>
      <span className="font-mono" title="Container image version">
        {loaded ?? '…'}
      </span>
    </footer>
  );
}

/** Header bell surfacing the rep's own overdue follow-ups, SLA-risk, and newly-assigned leads. */
function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.notifications(),
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  });
  const total = data?.total ?? 0;
  const anyItems =
    !!data &&
    (data.followUpsDue.count > 0 || data.slaRisk.count > 0 || data.newlyAssigned.count > 0);
  const go = (id: string) => {
    setOpen(false);
    navigate(`/scripts/${id}`);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative rounded-md px-2 py-1 hover:bg-white/10"
        title="Notifications"
        aria-label="Notifications"
      >
        🔔
        {total > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-gold px-1 text-[10px] font-bold text-navy">
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-50 mt-2 w-72 rounded-lg border border-slate-200 bg-white p-2 text-navy shadow-lg">
            <NotifSection title="Follow-ups due" group={data?.followUpsDue} onGo={go} />
            <NotifSection title="SLA risk — untouched 7d+" group={data?.slaRisk} onGo={go} />
            <NotifSection title="Newly assigned" group={data?.newlyAssigned} onGo={go} />
            {!anyItems && (
              <p className="px-2 py-4 text-center text-xs text-greyish">You're all caught up 🎉</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function NotifSection({
  title,
  group,
  onGo,
}: {
  title: string;
  group?: NotificationGroup;
  onGo: (id: string) => void;
}) {
  if (!group || group.count === 0) return null;
  return (
    <div className="mb-1">
      <div className="px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-greyish">
        {title} ({group.count})
      </div>
      {group.items.map((i) => (
        <button
          key={i.id}
          onClick={() => onGo(i.id)}
          className="block w-full truncate rounded px-2 py-1 text-left text-xs hover:bg-slate-50"
        >
          {i.company || '—'}
        </button>
      ))}
      {group.count > group.items.length && (
        <div className="px-2 py-0.5 text-[11px] text-greyish">
          +{group.count - group.items.length} more
        </div>
      )}
    </div>
  );
}

/** Global typeahead search across leads, campaigns, quick answers, and (managers) users. */
function GlobalSearch() {
  const navigate = useNavigate();
  const setLeadFilters = useLeadQueue((s) => s.setFilters);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q.trim()), 250);
    return () => window.clearTimeout(t);
  }, [q]);

  const { data } = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => api.search(debounced),
    enabled: debounced.length >= 2,
  });

  const close = () => setOpen(false);
  const reset = () => {
    setOpen(false);
    setQ('');
  };
  const goAllLeads = () => {
    const term = q.trim();
    reset();
    navigate(`/leads${term ? `?q=${encodeURIComponent(term)}` : ''}`);
  };
  const goCampaign = (name: string) => {
    setLeadFilters({ campaign: name });
    reset();
    navigate('/leads');
  };
  const hasResults =
    !!data &&
    (data.leads.length > 0 ||
      data.campaigns.length > 0 ||
      data.quickAnswers.length > 0 ||
      data.users.length > 0);

  return (
    <div className="relative hidden md:block">
      <input
        className="w-56 rounded-md border border-white/25 bg-white/10 px-2 py-1 text-xs text-white placeholder:text-slate-300"
        placeholder="🔎 Search leads, campaigns… ↵"
        value={q}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') goAllLeads();
          else if (e.key === 'Escape') close();
        }}
      />
      {open && debounced.length >= 2 && (
        <>
          <div className="fixed inset-0 z-40" onClick={close} />
          <div className="absolute right-0 z-50 mt-2 max-h-[70vh] w-80 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2 text-navy shadow-lg">
            {!hasResults ? (
              <p className="px-2 py-3 text-center text-xs text-greyish">No matches for “{debounced}”.</p>
            ) : (
              <>
                {data!.leads.length > 0 && (
                  <SearchGroup title="Leads">
                    {data!.leads.map((l) => (
                      <SearchRow
                        key={l.id}
                        onClick={() => {
                          reset();
                          navigate(`/scripts/${l.id}`);
                        }}
                        main={l.company || '—'}
                        sub={[l.contact, l.phone, l.status].filter(Boolean).join(' · ')}
                      />
                    ))}
                  </SearchGroup>
                )}
                {data!.campaigns.length > 0 && (
                  <SearchGroup title="Campaigns">
                    {data!.campaigns.map((c) => (
                      <SearchRow key={c.name} onClick={() => goCampaign(c.name)} main={c.name} />
                    ))}
                  </SearchGroup>
                )}
                {data!.quickAnswers.length > 0 && (
                  <SearchGroup title="Quick Answers">
                    {data!.quickAnswers.map((qa) => (
                      <SearchRow
                        key={qa.id}
                        onClick={() => {
                          reset();
                          navigate('/quick-answers');
                        }}
                        main={qa.question}
                      />
                    ))}
                  </SearchGroup>
                )}
                {data!.users.length > 0 && (
                  <SearchGroup title="Users">
                    {data!.users.map((u) => (
                      <SearchRow
                        key={u.id}
                        onClick={() => {
                          reset();
                          navigate('/admin');
                        }}
                        main={u.name}
                        sub={ROLE_LABEL[u.role as Role] ?? u.role}
                      />
                    ))}
                  </SearchGroup>
                )}
                <button
                  onClick={goAllLeads}
                  className="mt-1 block w-full rounded px-2 py-1.5 text-left text-xs font-semibold text-navy hover:bg-slate-50"
                >
                  See all lead matches for “{debounced}” →
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SearchGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-1">
      <div className="px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-greyish">{title}</div>
      {children}
    </div>
  );
}

function SearchRow({ main, sub, onClick }: { main: string; sub?: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="block w-full rounded px-2 py-1 text-left text-xs hover:bg-slate-50"
    >
      <div className="truncate font-semibold text-navy">{main}</div>
      {sub && <div className="truncate text-[11px] text-greyish">{sub}</div>}
    </button>
  );
}

const NAV_COLLAPSED_KEY = 'leadgenos.navCollapsed';

export function Shell({ children }: { children: ReactNode }) {
  const { user, isManagerView, isAdminView, actingAs, setActingAs, logout } = useSession();
  const navigate = useNavigate();
  const [navCollapsed, setNavCollapsed] = useState(
    () => localStorage.getItem(NAV_COLLAPSED_KEY) === '1',
  );

  const toggleNav = () => {
    setNavCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(NAV_COLLAPSED_KEY, next ? '1' : '0');
      return next;
    });
  };

  const onLogout = async () => {
    await logout();
    navigate('/');
  };

  const today = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(new Date());

  const visible = NAV.filter(
    (n) => (!n.managerOnly || isManagerView()) && (!n.adminOnly || isAdminView()),
  );

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-40 flex items-center gap-3 bg-navy px-4 py-3 text-white shadow">
        <div className="text-lg font-extrabold tracking-wide">
          Lead<span className="text-gold">GENOS</span>
        </div>
        <span className="chip hidden md:inline">CGM Lead Generation</span>

        <div className="ml-auto flex items-center gap-3 text-sm">
          <GlobalSearch />
          <span className="hidden text-xs text-slate-300 lg:inline">{today}</span>
          <NotificationBell />
          <ViewAsControl />
          <span className="hidden sm:inline">
            {user?.name} · <span className="text-gold">{user ? ROLE_LABEL[user.role] : ''}</span>
          </span>
          <button onClick={onLogout} className="btn-ghost !py-1 !text-navy">
            Sign out
          </button>
        </div>
      </header>

      <UpdateBanner />

      {actingAs && (
        <div className="flex items-center justify-center gap-3 bg-gold/90 px-4 py-1.5 text-xs font-semibold text-navy">
          👁 Viewing as {actingAs.name} (rep view)
          <button
            className="rounded bg-navy px-2 py-0.5 text-white"
            onClick={() => setActingAs(null)}
          >
            Exit rep view
          </button>
        </div>
      )}

      <div className="flex flex-1">
        <nav
          className={`hidden shrink-0 flex-col border-r border-slate-200 bg-white p-3 transition-[width] duration-200 sm:flex ${
            navCollapsed ? 'w-16' : 'w-52'
          }`}
        >
          {visible.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/' || n.to === '/admin'}
              title={navCollapsed ? n.label : undefined}
              className={({ isActive }) =>
                `mb-1 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${
                  navCollapsed ? 'justify-center' : ''
                } ${isActive ? 'bg-skylight text-navy' : 'text-slate-600 hover:bg-slate-50'}`
              }
            >
              <span className="text-base leading-none">{n.icon}</span>
              {!navCollapsed && <span className="truncate">{n.label}</span>}
            </NavLink>
          ))}

          <button
            onClick={toggleNav}
            title={navCollapsed ? 'Expand menu' : 'Collapse menu'}
            aria-label={navCollapsed ? 'Expand menu' : 'Collapse menu'}
            className={`mt-auto flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-50 ${
              navCollapsed ? 'justify-center' : ''
            }`}
          >
            <span className="text-base leading-none">{navCollapsed ? '»' : '«'}</span>
            {!navCollapsed && <span>Collapse</span>}
          </button>
        </nav>

        <main className="flex-1 overflow-x-hidden p-4 sm:p-6">{children}</main>
      </div>

      <VersionFooter />

      {/* Mobile bottom nav (scrolls horizontally to fit the full bar) */}
      <nav className="flex overflow-x-auto border-t border-slate-200 bg-white sm:hidden">
        {visible.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === '/' || n.to === '/admin'}
            className={({ isActive }) =>
              `flex min-w-[64px] flex-1 flex-col items-center py-2 text-[11px] font-semibold ${
                isActive ? 'text-navy' : 'text-slate-500'
              }`
            }
          >
            <span className="text-base">{n.icon}</span>
            {n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
